import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Text,
  Spinner,
  Button,
  makeStyles,
  tokens,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Tooltip,
} from '@fluentui/react-components';
import {
  CheckmarkCircle20Regular,
  ErrorCircle20Regular,
  Warning20Regular,
  ArrowSync20Regular,
  ChevronDown20Regular,
  ChevronUp20Regular,
  PersonAdd20Regular,
  PersonDelete20Regular,
  PersonEdit20Regular,
  PeopleTeam20Regular,
  Building20Regular,
  DocumentSync20Regular,
  HatGraduation20Regular,
  PersonAccounts20Regular,
  Briefcase20Regular,
  Person20Regular,
  Copy20Regular,
  Search20Regular,
} from '@fluentui/react-icons';
import { EventTimeline } from './EventTimeline';
import {
  getMessageTypeCode,
  getUserTypeFromMessageType,
  operationInvolvesClass,
  isClassOperation,
  formatClassIdWithPrefix,
  type UserType,
} from '../../utils/operationFormatter';
import { educationService } from '../../services/educationService';
import type { LogEntry, EducationUser } from '../../types';

export interface OperationSummary {
  correlationId: string;
  operation: string;
  messageType: string;
  startTime: Date;
  endTime?: Date;
  duration?: string;
  status: 'success' | 'warning' | 'error' | 'processing';
  totalEvents: number;
  errorCount: number;
  warningCount: number;
  externalId?: string;
  displayName?: string;
  userPrincipalName?: string;
  hasDlqMessage: boolean;
  dlqMessageId?: string;
  maxRetryCount?: number;
  hasRetries?: boolean;
  hasDlq?: boolean;
  events?: LogEntry[];
  sectionId?: string;
}

interface OperationsTableProps {
  operations: OperationSummary[];
  loading?: boolean;
  onLoadEvents?: (correlationId: string) => Promise<LogEntry[]>;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  statusFilter?: string;
}

const useStyles = makeStyles({
  tableCard: {
    padding: '16px',
    marginTop: '16px',
  },
  table: {
    width: '100%',
  },
  compactCell: {
    fontSize: '12px',
  },
  correlationId: {
    fontFamily: 'monospace',
    fontSize: '11px',
    wordBreak: 'break-all',
  },
  iconCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '5px',
    flexShrink: 0,
  },
  iconGreen: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  iconRed: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  iconYellow: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground1,
  },
  iconBlue: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  iconGray: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  expandedRow: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  expandedContent: {
    padding: '16px',
  },
  timelineContainer: {
    marginTop: '12px',
  },
  correlationRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
    padding: '8px 12px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '4px',
    fontSize: '12px',
    flexWrap: 'wrap',
  },
  correlationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  correlationLabel: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  correlationValue: {
    fontFamily: 'monospace',
    color: tokens.colorNeutralForeground1,
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px',
    gap: '12px',
    alignItems: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px',
    color: tokens.colorNeutralForeground3,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  },
  loadMoreTrigger: {
    height: '20px',
    width: '100%',
  },
  actionsCell: {
    display: 'flex',
    gap: '4px',
  },
  userInfoRow: {
    marginTop: '8px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  },
});

// Mapeamento de messageType para entidade em português
const entityNames: Record<string, string> = {
  'NewStudent': 'Estudante',
  'UpdateStudent': 'Estudante',
  'DeleteStudent': 'Estudante',
  'NewStudentEnrollment': 'Estudante',
  'DeleteStudentEnrollment': 'Estudante',
  'NewTeacher': 'Professor',
  'UpdateTeacher': 'Professor',
  'DeleteTeacher': 'Professor',
  'NewTeacherRoster': 'Professor',
  'DeleteTeacherRoster': 'Professor',
  'NewStaff': 'Funcionario',
  'UpdateStaff': 'Funcionario',
  'DeleteStaff': 'Funcionario',
  'NewStaffRoster': 'Funcionario',
  'DeleteStaffRoster': 'Funcionario',
  // Section (backend) e Class (frontend) - ambos mapeiam para Turma
  'NewSection': 'Turma',
  'UpdateSection': 'Turma',
  'DeleteSection': 'Turma',
  'NewClass': 'Turma',
  'UpdateClass': 'Turma',
  'DeleteClass': 'Turma',
  'NewSchool': 'Escola',
  'UpdateSchool': 'Escola',
  'DeleteSchool': 'Escola',
};

// Função para obter ícone do tipo de operação
const getOperationIcon = (type: string) => {
  if (!type) return <DocumentSync20Regular />;
  const lowerType = type.toLowerCase();
  if (lowerType.includes('new') || lowerType.includes('create')) return <PersonAdd20Regular />;
  if (lowerType.includes('delete') || lowerType.includes('remove')) return <PersonDelete20Regular />;
  if (lowerType.includes('update') || lowerType.includes('edit')) return <PersonEdit20Regular />;
  if (lowerType.includes('section') || lowerType.includes('class') || lowerType.includes('roster') || lowerType.includes('enrollment')) return <PeopleTeam20Regular />;
  if (lowerType.includes('school')) return <Building20Regular />;
  return <DocumentSync20Regular />;
};

// Função para obter ícone do tipo de entidade (usuário ou não-usuário)
function getUserTypeIcon(userType: UserType) {
  switch (userType) {
    case 'student':
      return <HatGraduation20Regular />;
    case 'teacher':
      return <PersonAccounts20Regular />;
    case 'staff':
      return <Briefcase20Regular />;
    case 'class':
      return <PeopleTeam20Regular />;
    case 'school':
      return <Building20Regular />;
    default:
      return <Person20Regular />;
  }
}

// Interface para dados do usuário buscados no Graph
interface UserGraphData {
  displayName?: string;
  userPrincipalName?: string;
  classDisplayName?: string;
  loading?: boolean;
  error?: string;
}

export const OperationsTable: React.FC<OperationsTableProps> = ({
  operations,
  loading,
  onLoadEvents,
  onLoadMore,
  hasMore,
  loadingMore,
  statusFilter,
}) => {
  const styles = useStyles();
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState<LogEntry[]>([]);
  const [userGraphData, setUserGraphData] = useState<Map<string, UserGraphData>>(new Map());
  const [loadingUserData, setLoadingUserData] = useState<Set<string>>(new Set());
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Intersection Observer para scroll infinito
  useEffect(() => {
    if (!onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  // Função para buscar dados do usuário no Graph
  const fetchUserFromGraph = async (correlationId: string, externalId: string, userType: UserType, sectionId?: string) => {
    if (loadingUserData.has(correlationId) || userGraphData.has(correlationId)) {
      return;
    }

    setLoadingUserData(prev => new Set(prev).add(correlationId));
    setUserGraphData(prev => new Map(prev).set(correlationId, { loading: true }));

    try {
      let user: EducationUser | null = null;
      let classDisplayName: string | undefined;

      const cleanId = externalId.replace(/^(stu_|tea_|sta_|stf_)/i, '');

      switch (userType) {
        case 'student':
          user = await educationService.getUserByStudentExternalId(cleanId);
          break;
        case 'teacher':
          user = await educationService.getUserByTeacherExternalId(cleanId);
          break;
        case 'staff':
          user = await educationService.getUserByStaffExternalId(cleanId);
          break;
      }

      if (sectionId) {
        try {
          const cleanSectionId = sectionId.replace(/^(sec_|cls_)/i, '');
          const classData = await educationService.getClassByExternalId(cleanSectionId);
          if (classData) {
            classDisplayName = classData.displayName;
          }
        } catch {
          // Classe não encontrada
        }
      }

      const result: UserGraphData = {};
      if (user) {
        result.displayName = user.displayName;
        result.userPrincipalName = user.userPrincipalName;
      }
      if (classDisplayName) {
        result.classDisplayName = classDisplayName;
      }
      if (!user && !classDisplayName) {
        result.error = 'Dados não encontrados';
      }

      setUserGraphData(prev => new Map(prev).set(correlationId, result));
    } catch {
      setUserGraphData(prev => new Map(prev).set(correlationId, { error: 'Erro ao buscar' }));
    } finally {
      setLoadingUserData(prev => {
        const next = new Set(prev);
        next.delete(correlationId);
        return next;
      });
    }
  };

  // Handler para expandir/colapsar linha
  const handleToggleExpand = async (correlationId: string, op: OperationSummary) => {
    if (expandedRow === correlationId) {
      setExpandedRow(null);
      return;
    }

    setExpandedRow(correlationId);

    // Carregar eventos se não estiverem carregados
    if (!op.events || op.events.length === 0) {
      if (onLoadEvents) {
        setLoadingTimeline(true);
        try {
          const events = await onLoadEvents(correlationId);
          setTimelineEvents(events);
        } catch {
          setTimelineEvents([]);
        } finally {
          setLoadingTimeline(false);
        }
      }
    } else {
      setTimelineEvents(op.events);
    }
  };

  // Formatar data/hora
  const formatDateTime = (date: Date) => {
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner label="Carregando operações..." size="medium" />
      </div>
    );
  }

  if (operations.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text size={400}>Nenhuma operação encontrada</Text>
      </div>
    );
  }

  return (
    <Card className={styles.tableCard}>
      <Table className={styles.table}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell style={{ width: '50px' }}>Status</TableHeaderCell>
            <TableHeaderCell style={{ width: '140px' }}>Data/Hora</TableHeaderCell>
            <TableHeaderCell style={{ width: '100px' }}>Tipo de Usuário</TableHeaderCell>
            <TableHeaderCell style={{ width: '140px' }}>Operação</TableHeaderCell>
            <TableHeaderCell style={{ width: '200px' }}>External ID</TableHeaderCell>
            <TableHeaderCell style={{ width: '80px' }}>Eventos</TableHeaderCell>
            <TableHeaderCell style={{ width: '100px' }}>Ações</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {operations.map((op) => {
            const isExpanded = expandedRow === op.correlationId;
            const entityName = entityNames[op.messageType] || 'Entidade';
            const typeCode = getMessageTypeCode(op.messageType);
            const userType = getUserTypeFromMessageType(op.messageType);
            const graphData = userGraphData.get(op.correlationId);
            const isLoadingUser = loadingUserData.has(op.correlationId);

            return (
              <React.Fragment key={op.correlationId}>
                <TableRow>
                  {/* Status */}
                  <TableCell>
                    <div className={styles.iconCell}>
                      <div className={`${styles.iconWrapper} ${
                        op.status === 'error' ? styles.iconRed :
                        op.status === 'warning' ? styles.iconYellow :
                        op.status === 'processing' ? styles.iconYellow :
                        styles.iconGreen
                      }`}>
                        {op.status === 'error' ? <ErrorCircle20Regular /> :
                         op.status === 'warning' ? <Warning20Regular /> :
                         op.status === 'processing' ? <ArrowSync20Regular /> :
                         <CheckmarkCircle20Regular />}
                      </div>
                    </div>
                  </TableCell>

                  {/* Data/Hora */}
                  <TableCell>
                    <span className={styles.compactCell}>
                      {formatDateTime(op.endTime || op.startTime)}
                    </span>
                  </TableCell>

                  {/* Entidade */}
                  <TableCell>
                    <div className={styles.iconCell}>
                      <div className={`${styles.iconWrapper} ${styles.iconBlue}`}>
                        {getUserTypeIcon(userType)}
                      </div>
                      <span>{entityName}</span>
                    </div>
                  </TableCell>

                  {/* Operação */}
                  <TableCell>
                    <div className={styles.iconCell}>
                      <div className={`${styles.iconWrapper} ${styles.iconGray}`}>
                        {getOperationIcon(op.messageType)}
                      </div>
                      <Tooltip content={`Tipo: ${typeCode}`} relationship="label">
                        <span>{op.messageType}</span>
                      </Tooltip>
                    </div>
                  </TableCell>

                  {/* External ID */}
                  <TableCell>
                    <div className={styles.iconCell}>
                      <span className={styles.compactCell}>{op.externalId || 'N/A'}</span>
                      {op.externalId && (
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={isLoadingUser ? <Spinner size="tiny" /> : <Search20Regular />}
                          title="Buscar dados no Graph"
                          disabled={isLoadingUser || userGraphData.has(op.correlationId)}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Para operações de classe (NewClass, UpdateClass), usar externalId como sectionId
                            const effectiveSectionId = isClassOperation(op.messageType) ? op.externalId : op.sectionId;
                            fetchUserFromGraph(op.correlationId, op.externalId!, userType, effectiveSectionId);
                          }}
                          style={{ minWidth: 'auto', padding: '2px' }}
                        />
                      )}
                    </div>
                  </TableCell>

                  {/* Eventos */}
                  <TableCell>
                    <div className={styles.iconCell}>
                      <span>{op.totalEvents}</span>
                      {op.hasDlq && (
                        <span style={{ marginLeft: '8px', fontSize: '11px', color: tokens.colorNeutralForeground2 }}>
                          DLQ
                        </span>
                      )}
                    </div>
                  </TableCell>

                  {/* Ações */}
                  <TableCell>
                    <div className={styles.actionsCell}>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={isExpanded ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
                        onClick={() => handleToggleExpand(op.correlationId, op)}
                      >
                        {isExpanded ? 'Ocultar' : 'Detalhes'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>

                {/* Linha expandida com Timeline */}
                {isExpanded && (
                  <TableRow key={`${op.correlationId}-expanded`} className={styles.expandedRow}>
                    <TableCell colSpan={7}>
                      <div className={styles.expandedContent}>
                        {/* CorrelationId e dados do Graph */}
                        <div className={styles.correlationRow}>
                          <div className={styles.correlationItem}>
                            <span className={styles.correlationLabel}>CorrelationId:</span>
                            <span className={styles.correlationValue}>{op.correlationId}</span>
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<Copy20Regular />}
                              title="Copiar"
                              onClick={() => navigator.clipboard.writeText(op.correlationId)}
                              style={{ minWidth: 'auto', padding: '2px' }}
                            />
                          </div>
                          {op.externalId && (
                            <div className={styles.correlationItem}>
                              <span className={styles.correlationLabel}>External ID:</span>
                              <span className={styles.correlationValue}>{op.externalId}</span>
                            </div>
                          )}
                          {operationInvolvesClass(op.messageType) && op.sectionId && (
                            <>
                              <span style={{ color: tokens.colorNeutralForeground3 }}>|</span>
                              <div className={styles.correlationItem}>
                                <span className={styles.correlationLabel}>Section ID:</span>
                                <span className={styles.correlationValue}>{formatClassIdWithPrefix(op.sectionId)}</span>
                              </div>
                            </>
                          )}
                          {op.hasRetries && (
                            <div className={styles.correlationItem}>
                              <span className={styles.correlationLabel}>Tentativas:</span>
                              <span>{op.maxRetryCount}</span>
                            </div>
                          )}
                        </div>

                        {/* Dados do Graph (se buscados) */}
                        {graphData && !graphData.loading && !graphData.error && (
                          <div className={styles.userInfoRow}>
                            {graphData.displayName && (
                              <div className={styles.correlationItem}>
                                <span className={styles.correlationLabel}>Nome:</span>
                                <span>{graphData.displayName}</span>
                              </div>
                            )}
                            {graphData.userPrincipalName && (
                              <div className={styles.correlationItem}>
                                <span className={styles.correlationLabel}>UPN:</span>
                                <span>{graphData.userPrincipalName}</span>
                              </div>
                            )}
                            {graphData.classDisplayName && (
                              <div className={styles.correlationItem}>
                                <span className={styles.correlationLabel}>Nome da Turma:</span>
                                <span>{graphData.classDisplayName}</span>
                              </div>
                            )}
                          </div>
                        )}
                        {graphData?.error && (
                          <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
                            {graphData.error}
                          </Text>
                        )}

                        {/* Timeline de eventos */}
                        <div className={styles.timelineContainer}>
                          {loadingTimeline ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                              <Spinner label="Carregando timeline..." />
                            </div>
                          ) : timelineEvents.length > 0 ? (
                            <EventTimeline
                              correlationId={op.correlationId}
                              events={timelineEvents}
                              statusFilter={statusFilter}
                            />
                          ) : (
                            <Text>Nenhum evento encontrado</Text>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>

      {/* Trigger para scroll infinito */}
      {hasMore && (
        <div ref={loadMoreRef} className={styles.loadMoreTrigger}>
          {loadingMore && (
            <div className={styles.loadMoreContainer}>
              <Spinner size="small" label="Carregando mais..." />
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default OperationsTable;
